<template>
    <div>
        <p>
            <span v-if="!testModeSmall">Texte - petit</span>
            <CodeGenerator
                v-model="text_small"
                :inline="testModeSmall"
                :build-request="buildTextSmallRequest"
                :validate-code="validateTemplateCode"
                code-language="html"
                modal-title="Génération du texte petit"
                prompt-placeholder="Ex: Afficher le nombre total de clients et leur CA moyen"
                ref="codeGeneratorSmall"
                @enter-test-mode="enterTestMode('small')">
                <template #options>
                    <p>
                        <b-checkbox style="display:inline" v-model="codegen_include_first_lines" />Envoyer la première ligne de chaque requête comme exemple
                    </p>
                </template>
            </CodeGenerator>
        </p>

        <div v-if="testModeSmall" class="codegen-test-mode-bar">
            <p class="text-muted small mb-2">
                <i class="fas fa-flask"></i> Mode test (texte petit) : navigation et enregistrement verrouillés. Prévisualisation disponible. Validez ou quittez.
            </p>
            <div class="d-flex justify-content-between">
                <b-button variant="outline-secondary" size="sm" @click="exitTestMode('small', false)">
                    <i class="fas fa-arrow-left"></i> Quitter sans valider
                </b-button>
                <b-button variant="success" size="sm" @click="exitTestMode('small', true)">
                    <i class="fas fa-check"></i> Valider et quitter le mode test
                </b-button>
            </div>
        </div>

        <div v-show="!testModeSmall" style="width: 100%; height: 300px; display: inline-block; position: relative;">
            <pre style="width: 100%; height: 100%" :id="'ace_editor_small-' + id"></pre>
        </div>

        <p>
            <span v-if="!testModeBig">Texte - grand</span>
            <CodeGenerator
                v-model="text_big"
                :inline="testModeBig"
                :build-request="buildTextBigRequest"
                :validate-code="validateTemplateCode"
                code-language="html"
                modal-title="Génération du texte grand"
                prompt-placeholder="Ex: Afficher la liste complète des clients avec leurs commandes"
                ref="codeGeneratorBig"
                @enter-test-mode="enterTestMode('big')">
                <template #options>
                    <p>
                        <b-checkbox style="display:inline" v-model="codegen_include_first_lines" />Envoyer la première ligne de chaque requête comme exemple
                    </p>
                </template>
            </CodeGenerator>
        </p>

        <div v-if="testModeBig" class="codegen-test-mode-bar">
            <p class="text-muted small mb-2">
                <i class="fas fa-flask"></i> Mode test (texte grand) : navigation et enregistrement verrouillés. Prévisualisation disponible. Validez ou quittez.
            </p>
            <div class="d-flex justify-content-between">
                <b-button variant="outline-secondary" size="sm" @click="exitTestMode('big', false)">
                    <i class="fas fa-arrow-left"></i> Quitter sans valider
                </b-button>
                <b-button variant="success" size="sm" @click="exitTestMode('big', true)">
                    <i class="fas fa-check"></i> Valider et quitter le mode test
                </b-button>
            </div>
        </div>

        <div v-show="!testModeBig" style="width: 100%; height: 300px; display: inline-block; position: relative;">
            <pre style="width: 100%; height: 100%" :id="'ace_editor_big-' + id"></pre>
        </div>

        <hr>

        <b-checkbox v-model="return_line">Retour à la ligne automatique</b-checkbox>

        <h3>Requêtes de base de données</h3>
        <p>Configurez des requêtes pour alimenter vos textes avec des données</p>

        <b-tabs v-model="queryTabIndex" content-class="mt-3">
            <b-tab
                :title="query.name"
                v-for="(query, idx) in queries"
                :key="query.id">
                <p>Nom de la requête (utilisé dans le template)</p>
                <b-input v-model="query.name" placeholder="Ex: clients, produits..."></b-input>
                <br>
                
                <p>Table</p>
                <b-form-select
                    v-model="query.table"
                    :options="tables"
                    @input="updateTable(idx)" />
                <br>

                <database
                    v-if="query.table"
                    :start_table="query.table"
                    :name="false"
                    :edit="false"
                    :ref="'database_' + idx"></database>

                <b-button
                    size="sm"
                    variant="danger"
                    class="mt-2"
                    @click="removeQuery(idx)">
                    Supprimer requête
                </b-button>
            </b-tab>
            <template #tabs-end>
                <b-nav-item
                    role="presentation"
                    @click.prevent="addQuery"
                    href="#"
                    ><b>+</b></b-nav-item
                >
            </template>
        </b-tabs>

        <hr>
        <p class="text-muted">
            <small>
                Utilisez les données dans votre texte avec la syntaxe : <code>${db["nom_requete"][0].colonne}</code><br>
                Exemple : <code>${db["clients"].length}</code> pour le nombre de résultats<br>
                Exemple : <code>${db["Ma requête 1"][0].nom}</code> pour accéder au premier résultat<br>
                Boucle : <code>${db["clients"].map(c => c.nom).join(', ')}</code><br>
                Note : Les noms de requêtes peuvent contenir des espaces, accents, etc.
            </small>
        </p>
    </div>

</template>

<script>
import database from "../../views/database.vue";
import CodeGenerator from "../CodeGenerator.vue";
import uniqueId from "lodash.uniqueid";
import store from "../../store";
import "../../../public/js/ace-builds/src-min-noconflict/ace.js";

    export default {
        components: {
            database,
            CodeGenerator
        },
        props: {
            params: {}
        },
        watch: {
            text_small() {
                this.syncAceSmallFromState();
            },
            text_big() {
                this.syncAceBigFromState();
            }
        },
        data: function () {
            return {
                id: uniqueId(""),
                ace_editor_small: null,
                ace_editor_big: null,
                text_small: "",
                text_big: "",
                queries: [],
                queryTabIndex: 0,
                tables: [],
                return_line: false,
                codegen_include_first_lines: false,

                // Mode "test en place" (TODO_CODEGEN_CHAT.md section 10) : indépendant
                // pour chaque éditeur (petit/grand texte), puisque ce sont deux
                // CodeGenerator et deux Ace totalement indépendants.
                testModeSmall: false,
                testModeBig: false
            };
		},
		methods: {
            validateTemplateCode(code) {
                // Pas de validation syntaxique JavaScript car c'est un template HTML/Lodash
                const errors = [];
                if (!code || !code.trim()) {
                    errors.push("Le template généré est vide.");
                }
                return errors;
            },
            initAceEditors() {
                if (typeof ace === "undefined")
                    return;

                const theme = (store && store.state && store.state.style && store.state.style.theme && store.state.style.theme.code)
                    ? store.state.style.theme.code
                    : "chrome";

                // Éditeur pour texte petit
                if (!this.ace_editor_small) {
                    const ace_editor_small = ace.edit("ace_editor_small-" + this.id);
                    this.ace_editor_small = ace_editor_small;
                    ace_editor_small.session.setMode("ace/mode/ejs");
                    ace_editor_small.setTheme("ace/theme/" + theme);
                    ace_editor_small.setOptions({
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: false
                    });
                    ace_editor_small.session.on("change", () => {
                        this.text_small = ace_editor_small.getValue();
                    });
                    this.syncAceSmallFromState(true);
                }

                // Éditeur pour texte grand
                if (!this.ace_editor_big) {
                    const ace_editor_big = ace.edit("ace_editor_big-" + this.id);
                    this.ace_editor_big = ace_editor_big;
                    ace_editor_big.session.setMode("ace/mode/ejs");
                    ace_editor_big.setTheme("ace/theme/" + theme);
                    ace_editor_big.setOptions({
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: false
                    });
                    ace_editor_big.session.on("change", () => {
                        this.text_big = ace_editor_big.getValue();
                    });
                    this.syncAceBigFromState(true);
                }
            },
            syncAceSmallFromState(force) {
                if (!this.ace_editor_small)
                    return;
                const currentValue = this.ace_editor_small.getValue();
                const nextValue = this.text_small || "";
                if (force || currentValue !== nextValue) {
                    this.ace_editor_small.setValue(nextValue, -1);
                }
            },
            syncAceBigFromState(force) {
                if (!this.ace_editor_big)
                    return;
                const currentValue = this.ace_editor_big.getValue();
                const nextValue = this.text_big || "";
                if (force || currentValue !== nextValue) {
                    this.ace_editor_big.setValue(nextValue, -1);
                }
            },
            buildTextSmallRequest(prompt, currentText) {
                return this.buildTextRequest(prompt, currentText, "petit");
            },
            buildTextBigRequest(prompt, currentText) {
                return this.buildTextRequest(prompt, currentText, "grand");
            },
            buildTextRequest(prompt, currentText, textType) {
                let queriesInfo = "";
                let examplesBlock = "";

                if (this.queries && this.queries.length > 0) {
                    queriesInfo = "\n\nRequêtes de base de données disponibles:\n";
                    this.queries.forEach(query => {
                        queriesInfo += `- ${query.name} : accessible via \${db["${query.name}"]}\n`;
                    });
                    queriesInfo += "\nChaque requête retourne un tableau d'objets. Utilisez [0] pour le premier résultat.\n";
                    queriesInfo += "Propriétés utiles: .length pour compter, .map() pour boucler, .join() pour concaténer.\n";

                    if (this.codegen_include_first_lines) {
                        examplesBlock = "\n\nExemples de données (première ligne de chaque requête):\n";
                        this.queries.forEach((query, idx) => {
                            // Récupérer les données depuis le composant database
                            const ref = this.$refs['database_' + idx];
                            const data = ref && ref[0] && ref[0].data ? ref[0].data : null;
                            
                            if (data && data.length > 0) {
                                try {
                                    const example = JSON.stringify(data[0], null, 2);
                                    const truncated = example.length > 100000 ? example.substring(0, 100000) + "\n... (tronqué)" : example;
                                    examplesBlock += `\n<BEGIN_EXAMPLE name="${query.name}">\n${truncated}\n</END_EXAMPLE>\n`;
                                } catch (e) {
                                    // Ignorer les erreurs de sérialisation
                                }
                            }
                        });
                    }
                }

                const request = `Tu es un assistant expert pour créer des templates d'affichage de données.

Contexte:
- Type de texte: ${textType}
- Format: Template Lodash (syntaxe \${...})
- Le template sera compilé et évalué avec lodash
- Framework CSS: Bootstrap 4 (utilise les classes Bootstrap pour le style)

Variables disponibles:
- db : objet contenant toutes les requêtes de base de données${queriesInfo}${examplesBlock}

Contraintes:
- Interdit: markdown, fences de code autour du texte.
- Respecte le contrat de format de réponse donné dans les instructions système (code seul, code + explication, ou question de clarification selon le mode sélectionné).
- Le texte peut contenir du HTML simple (br, strong, em, span avec classes Bootstrap)
- IMPORTANT: N'utilise JAMAIS de template strings JavaScript (backticks \`) dans les expressions \${...}
- Pour concaténer des strings, utilise l'opérateur + au lieu des backticks
- INTERDIT: Fonctions immédiatement invoquées (IIFE) comme (function(){...})() dans \${...}
- Pour la logique complexe, utilise la syntaxe Lodash complète:
  * <% code %> pour les instructions (if, for, var, etc.)
  * <%= expression %> pour afficher une valeur
  * \${expression} uniquement pour des expressions simples

Règles:
- Si du texte existe déjà, applique la demande EN MODIFIANT ce texte et renvoie le texte COMPLET final
- Sois concis pour le texte petit, plus détaillé pour le texte grand
- Gère les cas où les données sont vides ou undefined
- Utilise des conditions ternaires pour éviter les erreurs: \${db["query"] && db["query"][0] ? db["query"][0].field : "N/A"}
- Pour les map/join, concatène avec + : \${array.map(a => a.date + " - " + a.prix).join("<br>")}
- Pour la logique avec plusieurs étapes, préfère <% var x = ...; %><%= x %> plutôt que \${...}
- Le code doit être clair et lisible : n'hésite pas à utiliser plusieurs lignes et des indentations si nécessaire

TEXTE_ACTUEL:
<BEGIN_CODE>
${currentText || ""}
</BEGIN_CODE>

DEMANDE UTILISATEUR:
${prompt}
`;

                return request;
            },
            addQuery() {
                this.queries.push({
                    id: Date.now().toString(),
                    name: "query_" + (this.queries.length + 1),
                    table: null,
                    config: null
                });
                this.queryTabIndex = this.queries.length - 1;
            },
            removeQuery(idx) {
                this.queries.splice(idx, 1);
            },
            async updateTable(idx) {
                // Attendre que le composant soit monté
                await this.$nextTick();
                
                const ref = this.$refs['database_' + idx];
                if (ref && ref[0] && ref[0].init) {
                    await ref[0].init(this.queries[idx].table);
                }
            },
            updateQuery(idx, data) {
                // Cette méthode n'est plus nécessaire car on utilise save()
            },
            // which: 'small' | 'big'
            enterTestMode(which) {
                if (which === 'small') this.testModeSmall = true;
                else this.testModeBig = true;
                this.$emit('codegen-test-mode', true);
            },
            exitTestMode(which, apply) {
                var refName = which === 'small' ? 'codeGeneratorSmall' : 'codeGeneratorBig';
                var cg = this.$refs[refName];
                if (apply && cg && cg.lastGeneratedCode) {
                    if (cg.validation_errors && cg.validation_errors.length > 0) {
                        this.$notify({
                            group: "info",
                            type: "error",
                            text: "Le code généré contient des erreurs de validation, corrigez-les avant de valider.",
                            duration: 4000
                        });
                        return;
                    }
                    cg.applyGeneratedCode();
                }
                if (which === 'small') this.testModeSmall = false;
                else this.testModeBig = false;

                // On ne prévient le parent que si aucun des deux modes de test n'est
                // plus actif (sinon l'autre éditeur peut encore être en cours de test).
                if (!this.testModeSmall && !this.testModeBig)
                    this.$emit('codegen-test-mode', false);

                this.$nextTick(() => {
                    this.syncAceSmallFromState(true);
                    this.syncAceBigFromState(true);
                    if (this.ace_editor_small)
                        this.ace_editor_small.resize();
                    if (this.ace_editor_big)
                        this.ace_editor_big.resize();
                });
            },
            async save()
            {
                // Sauvegarder la configuration de chaque requête
                for (let i = 0; i < this.queries.length; i++) {
                    const ref = this.$refs['database_' + i];
                    
                    if (ref && ref[0] && ref[0].save) {
                        this.queries[i].config = await ref[0].save();
                    }
                }

                // Exclure le champ "data" de chaque query (peut être lourd)
                const queriesWithoutData = this.queries.map(q => ({
                    id: q.id,
                    name: q.name,
                    table: q.table,
                    config: q.config
                }));

                const payload = {
                    text_small: (this.testModeSmall && this.$refs.codeGeneratorSmall && this.$refs.codeGeneratorSmall.lastGeneratedCode)
                        ? this.$refs.codeGeneratorSmall.lastGeneratedCode
                        : this.text_small,
                    text_big: (this.testModeBig && this.$refs.codeGeneratorBig && this.$refs.codeGeneratorBig.lastGeneratedCode)
                        ? this.$refs.codeGeneratorBig.lastGeneratedCode
                        : this.text_big,
                    queries: queriesWithoutData,
                    return_line: this.return_line
                };

                // Sauvegarder l'état des générateurs de code (section 3 : conversations
                // multi-tours, cf CodeGenerator.vue::save() / TODO_CODEGEN_CHAT.md).
                // Persistance S3 (section 4) non câblée ici : les chats restent
                // stockés en entier dans le JSON du dashboard (pas de "loadChat"/
                // "deleteChat" fournis à CodeGenerator).
                if (this.$refs.codeGeneratorSmall) {
                    const smallState = this.$refs.codeGeneratorSmall.save({ include_first_lines: this.codegen_include_first_lines });
                    if (smallState.chats && smallState.chats.length > 0)
                        payload.codegen_small_chats = smallState.chats;
                    if (smallState.extraData)
                        payload.codegen_small_extraData = smallState.extraData;
                }

                if (this.$refs.codeGeneratorBig) {
                    const bigState = this.$refs.codeGeneratorBig.save({ include_first_lines: this.codegen_include_first_lines });
                    if (bigState.chats && bigState.chats.length > 0)
                        payload.codegen_big_chats = bigState.chats;
                    if (bigState.extraData)
                        payload.codegen_big_extraData = bigState.extraData;
                }

                return payload;
            },
            async load(data)
            {
                this.text_small = data.text_small || "";
                this.text_big = data.text_big || "";
                this.queries = data.queries || [];
                this.return_line = data.return_line || false;

                // Charger l'état des générateurs de code
                await this.$nextTick();
                if (this.$refs.codeGeneratorSmall) {
                    const loadedState = this.$refs.codeGeneratorSmall.load({
                        chats: data.codegen_small_chats || [],
                        // Compat. ascendante : anciens dashboards enregistrés avant la
                        // section 3 (multi-chat), avec seulement les 3 champs plats.
                        prompt: data.codegen_small_prompt || "",
                        codeBeforeGeneration: data.codegen_small_codeBeforeGeneration || "",
                        lastGeneratedCode: data.codegen_small_lastGeneratedCode || "",
                        extraData: data.codegen_small_extraData || {}
                    });
                    if (loadedState && loadedState.extraData && loadedState.extraData.include_first_lines !== undefined) {
                        this.codegen_include_first_lines = loadedState.extraData.include_first_lines;
                    }
                }

                if (this.$refs.codeGeneratorBig) {
                    this.$refs.codeGeneratorBig.load({
                        chats: data.codegen_big_chats || [],
                        prompt: data.codegen_big_prompt || "",
                        codeBeforeGeneration: data.codegen_big_codeBeforeGeneration || "",
                        lastGeneratedCode: data.codegen_big_lastGeneratedCode || "",
                        extraData: data.codegen_big_extraData || {}
                    });
                }

                if (this.queries.length === 0) return;
                
                // Attendre que les composants soient créés
                await this.$nextTick();
                await this.$nextTick();
                
                // Fonction pour attendre qu'un composant database soit complètement initialisé
                const waitForDatabaseInit = async (ref, maxWait = 3000) => {
                    const startTime = Date.now();
                    while (Date.now() - startTime < maxWait) {
                        if (ref && ref[0] && ref[0].columns && ref[0].columns.length > 0) {
                            return true;
                        }
                        await new Promise(resolve => setTimeout(resolve, 50));
                    }
                    return false;
                };
                
                // Charger la configuration de chaque requête
                for (let i = 0; i < this.queries.length; i++) {
                    if (!this.queries[i].config) continue;
                    
                    // Activer l'onglet pour que le composant soit visible et initialisé
                    this.queryTabIndex = i;
                    await this.$nextTick();
                    
                    const ref = this.$refs['database_' + i];
                    
                    if (ref && ref[0]) {
                        // Attendre que le composant ait fini son init() initial
                        const initialized = await waitForDatabaseInit(ref);
                        
                        if (initialized) {
                            await ref[0].load(this.queries[i].config, this.queries[i].table);
                        }
                    }
                }
                
                // Revenir au premier onglet
                this.queryTabIndex = 0;
                
                // Synchroniser les éditeurs ACE avec les valeurs chargées
                await this.$nextTick();
                this.syncAceSmallFromState(true);
                this.syncAceBigFromState(true);
            }
		},
		mounted: async function () {
            // Initialiser les éditeurs ACE
            this.initAceEditors();

            // Charger les tables disponibles
            var response = await this.$store.getters.getClient;
            if (response && response.details && response.details.tables) {
                this.tables = response.details.tables.map(t => t.name);
            }

            if (this.params)
                this.load(this.params)
		},
        beforeDestroy() {
            if (this.ace_editor_small) {
                try {
                    this.ace_editor_small.destroy();
                    this.ace_editor_small.container.remove();
                } catch (e) {}
                this.ace_editor_small = null;
            }
            if (this.ace_editor_big) {
                try {
                    this.ace_editor_big.destroy();
                    this.ace_editor_big.container.remove();
                } catch (e) {}
                this.ace_editor_big = null;
            }
        }
    }


</script>